import { ColumnBoxFlexFullWidth, FlexBox, FlexBoxFullWidth } from './boxes';
import { Box, IconButton, MenuItem, Tooltip, useTheme } from '@mui/material';
import { ChangeEvent, useCallback, useState } from 'react';
import { useDebounceCallback } from '../../hooks/useDebounceCallback';
import { Backspace, FilterList } from '@mui/icons-material';
import { ISearchHeaderProps } from '../../interfaces/searchHeaderProps';
import { BaseObj } from '../../types/baseObj';
import { SearchInput } from './SearchInput';
import { TimezoneAutocomplete } from './TimezoneAutocomplete';
import { AppDatePicker } from './AppDatePicker';
import LanguageAutocomplete from './LanguageAutocomplete';

const SearchHeader = ({ params, setParams, inputProps, initialState, initialExpanded = false }: ISearchHeaderProps) => {
  const theme = useTheme();
  const [debounceValues, setDebounceValues] = useState(params);
  const [expanded, setExpanded] = useState(initialExpanded);
  const toggleExpand = useCallback(() => setExpanded(prev => !prev), []);
  const debounceHandler = useDebounceCallback({
    delay: 700,
    callback: (change: any) => setParams((prev: any) => ({ ...prev, ...change })),
  });

  const clearFilters = useCallback(() => {
    setDebounceValues(initialState);
    setParams(initialState);
  }, [setParams, initialState]);

  // This handler catch onBlur event in order to populate values when multiple autocomplete action is triggered
  const forceUpdateHandler = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    if (value === '') {return;}
    const parsedVal = type === 'number' ? +value : value;
    setDebounceValues((prev: BaseObj) => ({ ...prev, [name]: parsedVal }));
    setParams((prev: BaseObj) => ({ ...prev, [name]: parsedVal }));
  }, [setParams]);

  const handleDebounceInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDebounceValues((prev: BaseObj) => ({ ...prev, [name]: value }));
    debounceHandler({ [name]: value });
  }, [setDebounceValues, debounceHandler]);

  const handleDebounceNumberInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const parsedVal = +value || '';
    setDebounceValues((prev: BaseObj) => ({ ...prev, [name]: parsedVal }));
    debounceHandler({ [name]: parsedVal });
  }, [setDebounceValues, debounceHandler]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setParams((prev: BaseObj) => ({ ...prev, [name]: value }));
  }, [setParams]);

  const handleLanguageAutocompleteChange = useCallback((val: BaseObj) => {
    setParams((prev: BaseObj) => ({ ...prev, ...val }));
  }, [setParams]);

  const handleDateChange = useCallback((date: string, namespace: string) => {
    setDebounceValues((prev: BaseObj) => ({ ...prev, [namespace]: date }));
    setParams((prev: BaseObj) => ({ ...prev, [namespace]: date }));
  }, [setParams]);

  return (
    <ColumnBoxFlexFullWidth position={'relative'} alignItems={'flex-start'}
                            boxShadow={expanded ? theme.shadows[2] : 'none'} p={2}
                            pb={expanded ? 6 : 2} borderRadius={theme.shape.borderRadius}
    >
      <FlexBoxFullWidth justifyContent={'space-between'}>
        <Box>
          <Tooltip title={'Filters'} placement={'right'}>
            <IconButton onClick={toggleExpand} sx={{ boxShadow: expanded ? 'none' : theme.shadows[3] }}>
              <FilterList color={'primary'} sx={{ fontSize: { mobile: 30, tablet: 40 } }}/>
            </IconButton>
          </Tooltip>
        </Box>
        {
          expanded &&
          <Box>
            <Tooltip title={'Clear Filters'}>
              <IconButton onClick={clearFilters}>
                <Backspace/>
              </IconButton>
            </Tooltip>
          </Box>
        }
      </FlexBoxFullWidth>
      <ColumnBoxFlexFullWidth alignItems={'center'} position={'relative'}>

        {
          expanded &&
          <FlexBoxFullWidth
            alignItems={{ mobile: 'flex-start', laptop: 'center' }} position={'relative'}
            borderRadius={theme.shape.borderRadius} flexDirection={{ mobile: 'column', laptop: 'row' }}
          >
            <FlexBoxFullWidth flexWrap={'wrap'}>
              {
                inputProps.map(input => {
                    if (input.isDatePicker) {
                      return (
                        <FlexBox key={input.name} flexDirection={{ mobile: 'column', laptop: 'row' }}
                                 width={{ mobile: '100%', laptop: 'unset' }}>
                          <Box mx={{ mobile: 0, laptop: 2 }} mt={{ mobile: 2, laptop: 1 }} key={input.name}>
                            <AppDatePicker
                              label={input.label}
                              dateValue={params[input.name]?.toString() || ''}
                              sx={{ minWidth: { mobile: '100%', laptop: 80 }, ...input.sx }}
                              handleChange={(val) => handleDateChange(val, input.name)}
                            />
                          </Box>

                        </FlexBox>
                      );
                    }
                    if (input.isTimezoneAutocomplete) {
                      return (
                        <FlexBox key={input.name} alignItems={'center'} width={{ mobile: '100%', laptop: 'initial' }}
                                 mx={{ mobile: 0, laptop: 2 }} my={1}>
                          <TimezoneAutocomplete value={params[input.name]} variant={'standard'}
                                                prePopulateClientTimezone={false}
                                                handleInputChange={handleInputChange}/>
                        </FlexBox>
                      );
                    }
                    if (input.isLanguageAutocomplete) {
                      return (
                        <FlexBox key={input.name} alignItems={'center'} width={{ mobile: '100%', laptop: 'initial' }}
                                 mx={{ mobile: 0, laptop: 2 }} my={1} minWidth={200}>
                          <LanguageAutocomplete languageCode={params[input.name]}
                                                onChange={handleLanguageAutocompleteChange}/>
                        </FlexBox>
                      );
                    }
                    return (
                      <SearchInput key={input.name + input.label} name={input.name} label={input.label}
                                   select={!!input.select}
                                   onChange={
                                     input.select
                                       ? handleInputChange
                                       : input.isNumber
                                         ? handleDebounceNumberInputChange
                                         : handleDebounceInputChange
                                   }                                   value={input.select
                                     ? input.select.list.length ? params[input.name] : ''
                                     : debounceValues[input.name] ?? ''}
                                   InputProps={{ inputProps: input.isNumber ? { min: 0 } : {} }}
                                   forceUpdate={forceUpdateHandler} type={input.isNumber ? 'number' : 'text'}
                                   SelectProps={{ MenuProps: { sx: { maxHeight: { mobile: 200, tablet: 300 } } } }}
                                   sx={{
                                     width: { mobile: '100%', laptop: 'initial' },
                                     minWidth: { mobile: '100%', laptop: 110 },
                                     mx: { mobile: 0, laptop: 2 },
                                     my: 1,
                                     ...input.sx
                                   }}
                      >
                        {
                          !!input.select && input.select.list.map(selectVal => (
                            <MenuItem key={input.name + input.select!.getValue(selectVal)}
                                      value={input.select!.getValue(selectVal)}>
                              {input.select!.getLabel(selectVal)}
                            </MenuItem>
                          ))
                        }
                      </SearchInput>
                    );
                  }
                )
              }
            </FlexBoxFullWidth>

          </FlexBoxFullWidth>
        }
      </ColumnBoxFlexFullWidth>

    </ColumnBoxFlexFullWidth>
  );
};

export default SearchHeader;